import { useState, useEffect, useRef } from 'react';
import { isEmpty, isEqual } from 'lodash-es';
import {
  AgxRow,
  AgxColumn,
  AgxBodyText,
  AgxFileUpload,
  DocumentTypes,
  UploadedDocument,
  Images,
  useDeepCompareEffect,
  EditablePdfDocumentModel,
  EditablePdfButtonGroup,
  CustomPdfScript,
  areArraysEqual,
} from '@urbanx/agx-ui-components';
import { useDialog } from '@urbanx/agx.ui-components2';
import EditPdfDialog from './components/EditPdfDialog';
import './EditablePdfUpload.scss';

interface Props {
  id: string;
  title?: string;
  readonly?: boolean;
  required?: boolean;
  requiredPdfFields?: string[];
  validate?: boolean;
  defaultValue?: EditablePdfDocumentModel[];
  campaignId: string;
  documentType: DocumentTypes;
  multiple?: boolean;
  buttonGroups: EditablePdfButtonGroup[];
  customScript?: CustomPdfScript;
  onValueChanged: (change: { id: string; value?: EditablePdfDocumentModel[] }) => void;
  onFileTypeSelected?: (select: string) => void;
  editFile?: (...args: any) => Promise<any>;
  uploadFile?: (...args: any) => Promise<any>;
  deleteFile?: (authToken: string, filePath: string) => Promise<number | undefined>;
  getAuthToken: () => Promise<string | void>;
  getFileLink?: (authToken: string, filePath: string) => Promise<any>;
}

const EditablePdfUpload = (props: Props) => {
  const {
    id,
    title = '',
    getAuthToken,
    readonly = false,
    defaultValue,
    campaignId,
    documentType,
    onValueChanged,
    required,
    requiredPdfFields = [],
    validate,
    uploadFile,
    editFile,
    deleteFile,
    getFileLink,
    buttonGroups,
    customScript,
    multiple,
  } = props;

  const [editablePdfUploadFiles, setEditablePdfUploadFiles] = useState<EditablePdfDocumentModel[]>(
    defaultValue ?? []
  );

  const editingFieldsRef = useRef<Record<string, string[]>>({});
  const [errors, setErrors] = useState<Record<string, string> | null>(null);

  const noDocumentFieldsErrorMessage = 'Check and save signature field mapping.';

  const areEditablePdfModelsEqual = (
    currentModels: EditablePdfDocumentModel[] = [],
    nextModels: EditablePdfDocumentModel[] = []
  ) =>
    areArraysEqual(currentModels, nextModels, (currentModel, nextModel) => {
      return (
        isEqual(currentModel?.uploadedDocument, nextModel?.uploadedDocument) &&
        isEqual(currentModel?.documentFields ?? [], nextModel?.documentFields ?? [])
      );
    });

  const mapUploadedDocumentsToEditablePdfModels = (files: UploadedDocument[] = []) =>
    files.map((uploadedDocument) => ({
      uploadedDocument,
      documentFields:
        editablePdfUploadFiles?.find(
          (editablePdfUploadFile) =>
            editablePdfUploadFile.uploadedDocument?.documentId === uploadedDocument?.documentId
        )?.documentFields ?? [],
    }));

  const handleFieldsUpdated = (documentId: string, fields: string[]) => {
    editingFieldsRef.current[documentId] = fields;
  };

  useEffect(() => {
    for (const editablePdfDocumentModel of defaultValue ?? []) {
      const documentId = editablePdfDocumentModel.uploadedDocument?.documentId;

      if (documentId) {
        editingFieldsRef.current[documentId] = editablePdfDocumentModel.documentFields ?? [];
      }
    }

    setEditablePdfUploadFiles((prev) =>
      areEditablePdfModelsEqual(prev, defaultValue ?? []) ? prev : (defaultValue ?? [])
    );
  }, [defaultValue]);

  const handleFileUpload = (files: UploadedDocument[]) => {
    const nextEditablePdfUploadFiles = mapUploadedDocumentsToEditablePdfModels(files);

    if (areEditablePdfModelsEqual(editablePdfUploadFiles, nextEditablePdfUploadFiles)) {
      return;
    }

    setEditablePdfUploadFiles(nextEditablePdfUploadFiles);

    onValueChanged({
      id,
      value: nextEditablePdfUploadFiles,
    });
  };

  const handleDocumentSaved = (documentId: string, uploadedDocument: UploadedDocument) => {
    setEditablePdfUploadFiles((prevEditablePdfUploadFiles) => {
      const updatedEditablePdfUploadFiles = prevEditablePdfUploadFiles.map(
        (editablePdfDocumentModel) =>
          editablePdfDocumentModel.uploadedDocument?.documentId === documentId
            ? {
                ...editablePdfDocumentModel,
                uploadedDocument,
                documentFields: editingFieldsRef.current[documentId],
              }
            : editablePdfDocumentModel
      );

      if (!areEditablePdfModelsEqual(prevEditablePdfUploadFiles, updatedEditablePdfUploadFiles)) {
        onValueChanged({ id, value: updatedEditablePdfUploadFiles });
      }

      return updatedEditablePdfUploadFiles;
    });
  };

  const editPdfDialog = useDialog(EditPdfDialog, {
    campaignId,
    buttonGroups,
    customScript,
    getAuthToken,
    getFileLink,
    editFile,
  });

  useDeepCompareEffect(() => {
    const arePdfsUploaded = editablePdfUploadFiles.some((file) => file.uploadedDocument);
    const areRequiredFieldsPresent = Object.values(editingFieldsRef.current).some(
      (documentFields) => requiredPdfFields.every((field) => documentFields.includes(field))
    );

    const errors: Record<string, string> = !areRequiredFieldsPresent
      ? editablePdfUploadFiles.reduce<Record<string, string>>((acc, file) => {
          const documentId = file.uploadedDocument?.documentId;

          if (documentId) {
            if (multiple && editablePdfUploadFiles.length > 1 && file.uploadedDocument?.fileName) {
              acc[documentId] =
                `${file.uploadedDocument.fileName} - ${noDocumentFieldsErrorMessage}`;
            } else {
              acc[documentId] = noDocumentFieldsErrorMessage;
            }
          }

          return acc;
        }, {})
      : {};

    if (required && !arePdfsUploaded) {
      setErrors(null);
    } else if (!areRequiredFieldsPresent) {
      setErrors(errors);
    } else {
      setErrors(null);
    }
  }, [validate, required, requiredPdfFields, editablePdfUploadFiles]);

  const isFileUploadRequired =
    !!required && !editablePdfUploadFiles.some((file) => file.uploadedDocument);

  return (
    <AgxColumn largeGap fill>
      <AgxFileUpload
        id={id}
        key={`${id}`}
        fileData={editablePdfUploadFiles?.map((doc) => doc?.uploadedDocument).filter(Boolean)}
        campaignId={campaignId}
        documentType={documentType}
        onValueChanged={({ value }) => handleFileUpload(value)}
        multiple={multiple ?? false}
        getAuthToken={getAuthToken}
        extensions={['pdf']}
        uploadFile={uploadFile}
        deleteFile={deleteFile}
        getFileLink={getFileLink}
        required={isFileUploadRequired}
        readonly={readonly}
        title={title}
        validate={validate}
        customIcon={
          <div data-testid={`agx-customIcon${id}`}>
            <Images.EditOutline />
          </div>
        }
        onCustomIconClick={(uploadedDocument) =>
          editPdfDialog.open({
            uploadedDocument,
            onFieldsUpdated: (fields) => handleFieldsUpdated(uploadedDocument.documentId, fields),
            onDocumentSaved: (editedDocument) =>
              handleDocumentSaved(uploadedDocument.documentId, editedDocument),
          })
        }
      />
      {validate &&
        !isEmpty(errors) &&
        Object.entries(errors).map(([key, error]) => (
          <AgxRow fill mediumGap key={`error-${key}`}>
            <Images.AlertCircle />
            <AgxBodyText small extraClasses="error" dataTestId={`error-${id}`}>
              {error}
            </AgxBodyText>
          </AgxRow>
        ))}
    </AgxColumn>
  );
};

export default EditablePdfUpload;
